/**
* DraggableComponent.js
* Created on Oct 22, 2013, 5:31:06 PM
*
* @author Javier Quevedo-Fernandez for Ranj
**/

import EventDispatcher from "../../util/EventDispatcher";
import { isArray, isNull } from "../../util/TypeUtil";
import DraggableComponentEvent from "./DraggableComponentEvent";


/* // PRE(Invariant): Container and skins must exist
* // The containers must have width and height properties (required for the drop border)
* 
* @constructor
* @param {Container} skin. The skin that will be dragable
* @param {Array} Array of Containers that the skin can be dropped in
* @param {Float} draggingAlpha. The alpha that the skin will have when being dragged
* @param {String} borderColor. The color for the target border when snapping
* @param {Float} borderWidth. The width for the target border when snapping
* @param {Bool} snapsToPoint. Determines if the draggable component should return to its origin 
* if when dropped wasn't in a target location
* @param {Number} pointX. The X return to location when not dragged on a target
* @param {Number} pointY. The Y return to location when not dragged on a target
**/
export default class DraggableComponent extends createjs.Container {
    constructor(skin, targets, draggingAlpha, borderColor, borderWidth, snapsToOrigin, originX, originY, hasBorder) {
        super();
        this._hasBorder = isNull(hasBorder) ? true : hasBorder;
        this._draggingAlpha = isNull(draggingAlpha) ? 0.5 : draggingAlpha;
        this._borderColor = isNull(borderColor) ? "#F00" : borderColor;
        this._borderWidth = isNull(borderWidth) ? 10 : borderWidth;
        this._snapsToOrigin = isNull(snapsToOrigin) ? false : snapsToOrigin;

        this._originX = originX;
        this._originY = originY;
        this.x = originX;
        this.y = originY;

        this._moveSpeed = 1500; // in pixels / second.  Speed at which the component
        // returns to its original location if the drag was not succesful

        this._targets = isArray(targets) ? targets : null;

        this._onMouseDown = this.onMouseDown.bind(this);
        this._onMouseDraggingMove = this.onMouseDraggingMove.bind(this);
        this._onMouseUp = this.onMouseUp.bind(this);

        this.eventDispatcher = new EventDispatcher();

        this.addChild(skin);
        this._skin = skin;
        //this.createBorder(this._borderColor, this._borderWidth);
        //this.addChild(this._border);
        //this._border.visible = false;
        this._skin.addEventListener("mousedown", this._onMouseDown);

        this._currentTarget;

    }

    onMouseDraggingMove({ stageX, stageY }) {
        this.eventDispatcher.dispatchEvent(DraggableComponentEvent.DRAG_UPDATE, { source: this });
        this.x = stageX - this._offX;
        this.y = stageY - this._offY;
        let onTarget = false;

        for (let i = 0; i < this._targets.length; i++) {
            const target = this._targets[i];
            const point = target.globalToLocal(stageX, stageY);

            if (target.hitTest(point.x, point.y)) {
                onTarget = true;
                if (!isNull(this._currentTarget)) {
                    this.eventDispatcher.dispatchEvent(DraggableComponentEvent.TARGET_EXIT, { source: this, destination: this._currentTarget });
                    this.removeBorder(this._currentTarget);
                }
                this._currentTarget = target;
                if (!this.hasBorder(target)) {
                    this.eventDispatcher.dispatchEvent(DraggableComponentEvent.TARGET_ENTER, { source: this, destination: this._currentTarget });
                    this.addBorder(target, this._borderColor, this._borderWidth);
                }
            } else {
                if (this._currentTarget === target) {
                    this.eventDispatcher.dispatchEvent(DraggableComponentEvent.TARGET_EXIT, { source: this, destination: this._currentTarget });
                    this.removeBorder(this._currentTarget);
                    this._currentTarget = undefined;
                }
            }
        }
    }

    addBorder(skin, color, thickness) {
        if (this._hasBorder) {
            const border = new createjs.Shape(
                new createjs.Graphics().beginStroke(color).setStrokeStyle(thickness).drawRect(0, 0, 2 * skin.regX + thickness, 2 * skin.regY + thickness));
            border.x = - thickness / 2;
            border.y = - thickness / 2;
            skin._dropBorder = border;
            skin.addChild(border);
        }
    }

    hasBorder({ _dropBorder }) {
        return !isNull(_dropBorder);
    }

    removeBorder(skin, color, thickness) {
        if (!isNull(skin._dropBorder)) {
            skin.removeChild(skin._dropBorder);
            skin._dropBorder = null;
        }
    }

    onMouseDown({ stageX, stageY }) {
        this._offX = stageX - this.x;
        this._offY = stageY - this.y;
        this._previousAlpha = this.alpha;
        this.alpha = this._draggingAlpha;
        this.eventDispatcher.dispatchEvent(DraggableComponentEvent.DRAG_BEGIN, { source: this });
        this.addEventListener("pressmove", this._onMouseDraggingMove);
        this.addEventListener("pressup", this._onMouseUp);
        // remove mouse down event? Not for now.

    }

    onMouseUp({ stageX, stageY }) {
        this.alpha = this._previousAlpha;
        this.removeEventListener("pressmove", this._onMouseDraggingMove);
        this.removeEventListener("pressup", this._onMouseUp);
        if (this._currentTarget) {
            var distance = Point.Distance(new Point(stageX, stageY), new Point(this._currentTarget.x - this._currentTarget.regX, this._currentTarget.y - this._currentTarget.regY));
            createjs.Tween.get(this).to({ x: this._currentTarget.x - this._currentTarget.regX, y: this._currentTarget.y - this._currentTarget.regY }, distance / this._moveSpeed * 1000).call(this.onDropCompleted);

        } else {
            if (this._snapsToOrigin) {
                var distance = Point.Distance(new Point(stageX, stageY), new Point(this._originX, this._originY));
                createjs.Tween.get(this).to({ x: this._originX, y: this._originY }, distance / this._moveSpeed * 1000).call(this.onDropCompleted);
            } else {
                this.onDropCompleted();
            }
        }
    }

    onDropCompleted() {
        this.alpha = this._previousAlpha;
        if (!isNull(this._currentTarget))
            this.removeBorder(this._currentTarget);
        this.eventDispatcher.dispatchEvent(DraggableComponentEvent.DRAG_END, { source: this, destination: this._currentTarget });
    }

    moveToSnappingPoint() {
        const distance = Point.Distance(new Point(this.x, this.y), new Point(this._originX, this._originY));
        createjs.Tween.get(this).to({ x: this._originX, y: this._originY }, distance / this._moveSpeed * 1000);
    }

    //    getOriginAlpha(){
    //        return this._originAlpha;
    //    };
    //
    //    setOriginAlpha(originAlpha){
    //        this._originAlpha = originAlpha;
    //    };

    getDraggingAlpha() {
        return this._draggingAlpha;
    }

    setDraggingAlpha(draggingAlpha) {
        this._draggingAlpha = draggingAlpha;
    }
}